import type { Mark, Node } from "prosemirror-model";
import { Mapping, Transform } from "prosemirror-transform";

/**
 * Takes a document and produces a mapping representing the document if all suggestions were
 * accepted. Specifically, in this mapping:
 *
 *   - Deletion marks: content is removed
 *   - Insertion marks: marks are stripped, content remains
 *   - Modification marks: marks are stripped (modifications are applied)
 *
 * Returns both the projected document and a mapping from projection positions to the original
 * marked-up document positions.
 *
 * To understand this, consider the following document:
 *
 *     Editor Doc (marked up)              Projection (clean)
 *     Hello <ins>beautiful </ins>world    Hello beautiful world
 *
 * This produces the following mapping:
 *
 *   - Projection pos 0  → Marked doc pos 0   (H)
 *   - Projection pos 6  → Marked doc pos 6   (b) - start of "beautiful"
 *   - Projection pos 16 → Marked doc pos 16  (w) - start of "world"
 *
 * In this simple case the mapping is 1:1, but consider if we had deletions:
 *
 *     Editor Doc                                   Projection
 *     Hello <del>old </del><ins>new </ins>world    Hello new world
 *
 * Now positions diverge significantly. This function will create a mapping as if the deletions were
 * performed.
 *
 * This function is primarily used to represent sequential "foreign" edits to a document's
 * underlying Markdown files. For example, if an AI TUI like `claude` produces some suggestions, and
 * then the user edits the same doc, and then the AI produces more suggestions, this function is
 * what allows us to amend the existing suggestion marks. By diffing against the projection (clean
 * doc), we get correct positions that can then be mapped back to the marked-up doc.
 */
export function buildAcceptedProjectionWithMapping(doc: Node): {
	projection: Node;
	projectionToMarked: Mapping;
} {
	const schema = doc.type.schema;
	const insertion = schema.marks["insertion"];
	const deletion = schema.marks["deletion"];
	const modification = schema.marks["modification"];

	if (!insertion || !deletion || !modification) {
		return {
			projection: doc,
			projectionToMarked: new Mapping(),
		};
	}

	const tr = new Transform(doc);

	applySuggestionsToTransform(doc, tr, insertion, deletion);
	applyModificationsToTransform(tr.doc, tr);

	const projectionToMarked = tr.mapping.invert();

	return {
		projection: tr.doc,
		projectionToMarked,
	};
}

/**
 * Finds the end position of a suggestion mark starting at a given resolved position.
 */
function findSuggestionMarkEnd($pos: ReturnType<Node["resolve"]>, markType: Mark["type"]): number {
	const doc = $pos.doc;
	let pos = $pos.pos;

	while (pos < doc.content.size) {
		const node = doc.nodeAt(pos);
		if (!node) {
			break;
		}

		if (node.isText) {
			const mark = markType.isInSet(node.marks);
			if (!mark) {
				break;
			}
			pos += node.nodeSize;
		} else if (node.isInline) {
			const mark = markType.isInSet(node.marks);
			if (!mark) {
				break;
			}
			pos += node.nodeSize;
		} else {
			break;
		}
	}

	return pos;
}

/**
 * Apply suggestions by removing deletion content and stripping insertion marks. This replicates the
 * logic from @handlewithcare/prosemirror-suggest-changes but operates on a Transform so we can
 * capture the mapping.
 */
function applySuggestionsToTransform(
	node: Node,
	tr: Transform,
	markTypeToApply: Mark["type"],
	markTypeToRevert: Mark["type"]
): void {
	const toApplyIsInSet = (marks: readonly Mark[]) => markTypeToApply.isInSet(marks);
	const toRevertIsInSet = (marks: readonly Mark[]) => markTypeToRevert.isInSet(marks);

	const isToApply = toApplyIsInSet(node.marks);
	if (isToApply) {
		if (node.isInline) {
			tr.removeMark(0, node.nodeSize, markTypeToApply);
		} else {
			tr.removeNodeMark(0, markTypeToApply);
		}
	}

	node.descendants((child, pos) => {
		const isToRevert = toRevertIsInSet(child.marks);
		const isChildToApply = toApplyIsInSet(child.marks);

		if (!isToRevert && !isChildToApply) {
			return true;
		}

		if (isToRevert) {
			const { pos: deletionFrom, deleted } = tr.mapping.mapResult(pos);
			if (deleted) {
				return false;
			}

			const deletionTo = findSuggestionMarkEnd(
				tr.doc.resolve(deletionFrom + child.nodeSize),
				markTypeToRevert
			);

			const prevChar =
				deletionFrom > 0
					? tr.doc.textBetween(deletionFrom - 1, deletionFrom, "x", "x")
					: "";
			const nextChar =
				deletionTo < tr.doc.content.size
					? tr.doc.textBetween(deletionTo, deletionTo + 1, "x", "x")
					: "";

			const addedRange = prevChar === " " && nextChar === " " ? 1 : 0;
			tr.deleteRange(deletionFrom, deletionTo + addedRange);

			return false;
		}

		const insertionFrom = tr.mapping.map(pos);
		const insertionTo = insertionFrom + child.nodeSize;

		if (child.isInline) {
			tr.removeMark(insertionFrom, insertionTo, markTypeToApply);
			if (child.text === "\u200B") {
				tr.delete(insertionFrom, insertionTo);
			}
		} else {
			tr.removeNodeMark(insertionFrom, markTypeToApply);
		}

		return true;
	});
}

/**
 * Apply modifications by removing modification marks. Modifications are considered "applied" - we
 * keep the current values.
 */
function applyModificationsToTransform(node: Node, tr: Transform): void {
	const modification = node.type.schema.marks["modification"];
	if (!modification) {
		return;
	}

	const isModification = modification.isInSet(node.marks);
	if (isModification) {
		let prevLength: number;
		do {
			prevLength = tr.steps.length;
			tr.removeNodeMark(0, modification);
		} while (tr.steps.length > prevLength);
	}

	node.descendants((child, pos) => {
		const isChildModification = modification.isInSet(child.marks);
		if (!isChildModification) {
			return true;
		}

		let prevLength: number;
		do {
			prevLength = tr.steps.length;
			tr.removeNodeMark(pos, modification);
		} while (tr.steps.length > prevLength);

		return true;
	});
}

/**
 * INEFFICIENTLY check if a document has any suggestion marks (insertion, deletion, modification).
 * This function is INEFFICIENT because it checks every node in the document. DO NOT USE THIS
 * FUNCTION IN ANY HOT PATHS.
 */
export function inefficientHasSuggestionMarks(doc: Node): boolean {
	const schema = doc.type.schema;
	const insertion = schema.marks["insertion"];
	const deletion = schema.marks["deletion"];
	const modification = schema.marks["modification"];

	if (!insertion || !deletion || !modification) {
		return false;
	}

	let found = false;

	doc.descendants((node) => {
		if (found) {
			return false;
		}

		if (
			insertion.isInSet(node.marks) ||
			deletion.isInSet(node.marks) ||
			modification.isInSet(node.marks)
		) {
			found = true;
			return false;
		}

		return true;
	});

	return found;
}
